import { Injectable } from '@angular/core';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { environment } from 'environments/environment';
import { Subject } from 'rxjs';
import { Notification } from './notification.model';
import * as uuid from 'uuid';
import { filter, map } from 'rxjs/operators';
import { notNull } from 'app/modules/shared/functions/filters';
import { notifications } from './notifications';
import { Store } from '@ngxs/store';
import { cloneDeep, startCase } from 'lodash';
import { Router } from '@angular/router';
import { format, isBefore, subMinutes, toDate } from 'date-fns';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  privateLenderUserChannelName: string;
  privateInstitutionChannelName: string;
  lenderUser: LenderUser;
  private notificationSubject = new Subject<Notification | null>();

  constructor(
    private pusherService: PusherService,
    private store: Store,
    private router: Router,
  ) {}

  subscribeToNotifications(lenderUser: LenderUser) {
    this.lenderUser = lenderUser;
    this.setChannelNames();
    this.pusherService
      .subscribeRaw(this.privateLenderUserChannelName)
      .bind_global((event: string, data: any) => this.notificationSubject.next(this.handlePrivateLenderUserEvent(event, data)));
    this.pusherService
      .subscribeRaw(this.privateInstitutionChannelName)
      .bind_global((event: string, data: any) => this.notificationSubject.next(this.handleDefaultEvent(event, data)));
  }

  unsubscribeFromNotifications() {
    this.pusherService.unsubscribeRaw(this.privateLenderUserChannelName);
    this.pusherService.unsubscribeRaw(this.privateInstitutionChannelName);
  }

  get notifications$() {
    return this.notificationSubject.asObservable().pipe(filter(notNull), map(notification => {
      return notification;
    }));
  }

  bindActions(notification: Notification, rehydrate: boolean = false) {
    if (rehydrate) {
      // To properly clone actions on notifications during page refresh
      notification.actions = cloneDeep(notifications.find(n => n.type === notification.type)?.actions);
    }
    if (notification.actions) {
      notification.actions = notification.actions.map(action => {
        action.function = action.function.bind(null, notification, { store: this.store, router: this.router });
        return action;
      });
    }
  }

  private setChannelNames() {
    this.privateLenderUserChannelName = `private-lender-user-${this.lenderUser.user.id}-${environment.environment}-notification-channel`;
    this.privateInstitutionChannelName = `private-lender-${this.lenderUser.institution_id}-${environment.environment}-notification-channel`;
  }

  private handlePrivateLenderUserEvent(event: string, data: any): Notification | null {
    let notification: Notification | undefined | null;
    switch (event) {
      case 'bulkAssignment':
        notification = this.handleBulkAssignment(data);
      break;
      case 'create':
        notification = this.handleCreateEvent(data);
      break;
      case 'sms':
        notification = this.handleSmsEvent(data);
      break;
      case 'message':
        notification = this.handleMessageEvent(data);
      break;
      case 'progress':
        notification = this.handleStatusChangeEvent(data);
      break;
      case 'offerCreated':
        notification = this.handleOfferEvent(event, data);
      break;
      case 'bp_upload_document':
        notification = this.handleBPDocumentEvent(event, data);
      break;
      case 'reminder':
        notification = this.handleReminderEvent(data);
      break;
      case 'openedMessage':
        notification = this.handleOpenedMessageEvent(data);
      break;
      case 'phone': // ignore phone notifications (handled in call-notification.service.ts)
        return null;
      default:
        notification = this.handleDefaultEvent(event, data);
      break;
    }
    if (notification) {
      notification.id = uuid.v4();
      notification.created = new Date();
      return notification;
    }
    return null;
  }

  private handleDefaultEvent(event: string, { title, borrowerId, text }) {
    const notification = notifications.find(n => n.type === event) as Notification;
    if (notification) {
      notification.title = title;
      notification.borrowerId = borrowerId;
      if (text) {
        notification.content = text.length > 160 ? `${text.substring(0, 160)}...` : text;
      }
      return notification;
    }
    return null;
  }

  private handleOpenedMessageEvent({ text, meta, title, borrowerId, phone }) {
    const notification = notifications.find(n => n.type === 'openedMessage') as Notification;
    if (notification) {
      notification.title = title;
      notification.borrowerId = borrowerId;
      notification.meta = {
        phone
      };
      notification.content = `${text}: <strong>${ meta.subject }</strong>`;
      return notification;
    }
    return null;
  }

  private handleReminderEvent({ meta, title, text, borrowerId }) {
    const event = meta.action ? `${meta.action}Reminder` : 'reminder';
    const notification = notifications.find(n => n.type === event) as Notification;
    if (notification) {
      notification.meta = {
        reminderId: meta.reminderId,
        phone: meta.phone
      };
      notification.title = title;
      notification.borrowerId = borrowerId;
      notification.content = `<b>Reminder Time:</b> ${format(toDate(meta.expiresTimestamp * 1000),'h:mma')}`;
      if (text) {
        notification.content += `<br/><b>Reason:</b> ${text}`;
      }
      return notification;
    }
  }

  private handleOfferEvent(event: string, data: any) {
    const notification = this.handleDefaultEvent(event, data);
    if (notification) {
      notification.content = `${data.title} has received an offer from ${data.meta.lenderName}.`;
      return notification;
    }
  }

  private handleBPDocumentEvent(event: string, data: any) {
    const notification = this.handleDefaultEvent(event, data);
    if (notification) {
      notification.content = `${data.text}a ${startCase(data.meta.category)}.`;
      return notification;
    }
  }

  private handleStatusChangeEvent({ title, borrowerId, meta, initiatorId }) {
    const notification = notifications.find(n => n.type === 'progress') as Notification;
    if (notification && initiatorId !== this.lenderUser.user.id) {
      notification.title = title;
      notification.borrowerId = borrowerId;
      notification.content = `${title} has been moved from <b>${startCase(meta.oldStatus)}</b> to <b>${startCase(meta.status)}</b>.`;
      return notification;
    }
  }

  private handleCreateEvent({ borrower, resourceName, object, initiatorId }) {
    if (!borrower || !borrower.id || !object) {
      return;
    }
    let notification: Notification | undefined;
    switch (resourceName) {
      case 'document':
        notification = this.handleDocumentNotification({ resourceName, object, initiatorId });
      break;
      case 'activity':
        notification = this.handleActivityNotification({ borrower, object, initiatorId });
      break;
    }
    if (notification) {
      notification.borrowerId = borrower.id;
      notification.title = borrower.name;
      return notification;
    }
  }

  private handleBulkAssignment({ assignmentCount, lenderUserId, resourceName }) {
    if (lenderUserId === this.lenderUser.user.id) {
      const notification = notifications.find(n => n.type === resourceName) as Notification;
      if (notification) {
        notification.content = `You have been assigned <b>${assignmentCount} new leads(s)</b>`;
        return notification;
      }
    }
  }

  private handleSmsEvent({ timestamp, borrowerId, text, title }) {
    const notification = notifications.find(n => n.type === 'sms') as Notification;
    notification.heading = isBefore(toDate(timestamp), subMinutes(new Date(Date.now()), 5))
      ? 'SMS History Updated'
      : 'Incoming Text';
    notification.borrowerId = borrowerId;
    notification.title = title;
    if (text) {
      notification.content = text.length > 160 ? `${text.substr(0, 160)}...` : text;
    }
    return notification;
  }

  private handleMessageEvent({ borrowerId, text, title }) {
    const notification = notifications.find(n => n.type === 'message') as Notification;
    notification.heading = 'Incoming Email';
    notification.borrowerId = borrowerId;
    notification.title = title;
    if (text) {
      notification.content = text.length > 160 ? `${text.substr(0, 160)}...` : text;
    }
    return notification;
  }

  private handleDocumentNotification({ resourceName, object, initiatorId }) {
    if (initiatorId !== this.lenderUser.user.id) {
      const notification = notifications.find(n => n.type === resourceName) as Notification;
      if (notification) {
        notification.content = `A document titled ${object.title} has been added.`;
        return notification;
      }
    }
  }

  private handleActivityNotification({ borrower, object, initiatorId }) {
    let notification: Notification | undefined;
    switch (object.type) {
      case 'assignment':
        if (object.newLenderUserId === this.lenderUser.user.id) {
          notification = notifications.find(n => n.type === object.type) as Notification;
          if (notification) {
            notification.content = `You are now assigned to <b>${borrower.name}</b>`;
            return notification;
          }
        }
      break;
      case 'note':
        if (initiatorId !== this.lenderUser.user.id) {
          notification = notifications.find(n => n.type === object.type) as Notification;
          if (notification) {
            notification.content = `${object.lenderUserName} just added a new note: <i>"${object.content.substr(0, 160)}...\"</i>`;
            return notification;
          }
        }
      break;
    }
  }
}
